/**
 * Lazy bridge to `sonner`.
 *
 * The toast runtime is ~12 kB gzip and is never needed for the first paint, so
 * it is dynamically imported the first time a toast is actually fired. The API
 * surface below mirrors exactly what the app uses, so call sites behave the
 * same as a direct `toast()` call.
 */
type SonnerModule = typeof import("sonner");

let mod: SonnerModule | null = null;
let pending: Promise<SonnerModule> | null = null;
let mounted = false;
let needToaster: (() => void) | null = null;
let mountResolve: (() => void) | null = null;
const mountedPromise = new Promise<void>((resolve) => {
  mountResolve = resolve;
});

/** AppShell registers here so the <Toaster/> can be mounted on demand. */
export function onToastsNeeded(cb: () => void) {
  needToaster = cb;
}

/** Called by the mounted <Toaster/> host so queued toasts can flush. */
export function markToasterMounted() {
  mounted = true;
  mountResolve?.();
}

function load(): Promise<SonnerModule> {
  if (!pending) pending = import("sonner").then((m) => ((mod = m), m));
  return pending;
}

/** Warms the chunk without showing anything (called once the app is idle). */
export function preloadToasts() {
  if (!mod && !pending) void load();
}

function run(fn: (t: SonnerModule["toast"]) => void) {
  if (mounted && mod) {
    fn(mod.toast);
    return;
  }
  needToaster?.();
  void Promise.all([load(), mountedPromise]).then(([m]) => fn(m.toast));
}

type Options = { id?: string; duration?: number };

export const toast = Object.assign(
  (message: string, options?: Options) => run((t) => t(message, options)),
  {
    success: (message: string, options?: Options) => run((t) => t.success(message, options)),
    error: (message: string, options?: Options) => run((t) => t.error(message, options)),
    loading: (message: string, options?: Options) => run((t) => t.loading(message, options)),
    dismiss: (id?: string) => run((t) => t.dismiss(id)),
  },
);